/**
 * Recovering from a denied location permission is browser-specific and there
 * is no API that navigates the user there — `permissions.request()` does not
 * exist, and re-calling `getCurrentPosition` after a denial is rejected
 * silently. The best available action is a link to instructions for the
 * browser the user is actually in, which is what this builds.
 */
const BROWSER_MATCHERS: Array<{ name: string; test: (ua: string) => boolean }> = [
  // Order matters: every Chromium browser's UA also contains "Chrome", and
  // Safari's appears in Chrome's too, so the specific names are tested first.
  { name: 'Edge', test: (ua) => /\bedg(?:e|a|ios)?\//i.test(ua) },
  { name: 'Opera', test: (ua) => /\b(?:opr|opera)\//i.test(ua) },
  { name: 'Samsung Internet', test: (ua) => /samsungbrowser\//i.test(ua) },
  { name: 'Firefox', test: (ua) => /\b(?:firefox|fxios)\//i.test(ua) },
  { name: 'Chrome', test: (ua) => /\b(?:chrome|crios|chromium)\//i.test(ua) },
  { name: 'Safari', test: (ua) => /safari\//i.test(ua) },
]

/** Best-effort browser name from a user-agent string; undefined when unrecognized. */
export function detectBrowserName(userAgent: string | undefined): string | undefined {
  if (!userAgent) return undefined
  return BROWSER_MATCHERS.find((matcher) => matcher.test(userAgent))?.name
}

/**
 * A web search rather than a hardcoded support URL: vendor help pages move,
 * and a stale link is worse than a search that always resolves to current
 * instructions. Schema authors that need somewhere specific set
 * `troubleshootingUrl`.
 */
export function buildLocationTroubleshootingUrl(browserName?: string): string {
  const query = ['browser location permission troubleshooting', browserName]
    .filter(Boolean)
    .join(' ')
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`
}

/** `troubleshootingUrl` when set, otherwise a search tailored to this browser. */
export function resolveTroubleshootingUrl(
  troubleshootingUrl: string | undefined,
  userAgent?: string,
): string {
  if (troubleshootingUrl) return troubleshootingUrl
  return buildLocationTroubleshootingUrl(detectBrowserName(userAgent))
}
